import { addMonths, addWeeks, isAfter, isValid, isWithinInterval, parse } from '@if/core/date'
import { EPaymentFrequency, EPaymentMethod, EPaymentPlan } from '@if/core/types'
import { formatUtcIsoDateTime } from '@if/core/utils'
import { MenuItem } from '@mui/material'
import { FormatPaymentPlanForList } from '@src/data/worksheet-selectors'
import { ETerms, MerchantId } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { MerchantPaymentPlan } from '@src/types/MerchantPaymentPlan'
import { UserDtoFromJwt } from '@src/types/User'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const usePaymentPlanList = (
  creditApp: CreditApplication,
  merchant: Merchant,
  paymentPlans?: MerchantPaymentPlan[],
) => {
  return useMemo(() => {
    const selectValueList: { label: string; value: string; id: string }[] = []

    selectValueList.push({
      value: EPaymentPlan.RegularDailyInterests,
      label: EPaymentPlan.RegularDailyInterests,
      id: merchant?.id,
    })

    const currentDate = new Date()
    const creationDate = new Date(creditApp?.createdOn)
    const DISTANT_FUTURE = new Date()
    DISTANT_FUTURE.setFullYear(DISTANT_FUTURE.getFullYear() + 100)

    const filteredPaymentPlans = paymentPlans?.filter((plan) => {
      const planActiveFrom = new Date(plan.activeFrom)
      const planExpiresOn = new Date(plan?.expiresOn ?? DISTANT_FUTURE)

      const currentUtcDateFormatted = new Date(formatUtcIsoDateTime(new Date()))
      return (
        ((isWithinInterval(creationDate, { start: planActiveFrom, end: planExpiresOn }) ||
          isWithinInterval(currentDate, { start: planActiveFrom, end: planExpiresOn })) &&
          isAfter(planExpiresOn, currentUtcDateFormatted)) ||
        creditApp.worksheet?.paymentPlanId === plan.id
      )
    })

    filteredPaymentPlans?.forEach((p) => {
      selectValueList.push({ value: p.id, label: FormatPaymentPlanForList(p), id: p.id })
    })

    return selectValueList
  }, [creditApp?.createdOn, creditApp.worksheet?.paymentPlanId, merchant?.id, paymentPlans])
}

export const usePaymentMerchantList = (
  creditApplication: CreditApplication,
  merchantsListFromParentId: Merchant[],
  merchant: Merchant | null,
  user: UserDtoFromJwt,
) => {
  const [merchantList, setMerchantList] = useState<Merchant[]>([])

  useEffect(() => {
    let newMerchantList: Merchant[] = []
    if (merchant?.forcePaymentToBorrower === true) {
      newMerchantList = [
        {
          id: MerchantId.PayCustomerByBankTransfer,
          defaultPaymentMethod: EPaymentMethod.BankTransfer,
          name: `${creditApplication.applicant.firstName} ${creditApplication.applicant.lastName}`,
        } as unknown as Merchant,
      ]
    } else if (creditApplication?.worksheet && user.isBannerUser) {
      const merchantIds = creditApplication.worksheet.merchantPayments.map((payment) => payment.merchantId)
      newMerchantList = merchantsListFromParentId.filter((m) => merchantIds.includes(m.id))
    } else if (merchant) {
      newMerchantList = [merchant]
    }
    return setMerchantList(newMerchantList)
  }, [creditApplication, merchant, merchantsListFromParentId, user.isBannerUser])
  return { merchantList, setMerchantList }
}

export const useProductsEarliestPaymentDate = () => {
  const getProductsEarliestPaymentDateGivenActivationDate = useCallback((activationDate: Date) => {
    const initialEarliestDateForFirstPayment = new Date(activationDate)
    initialEarliestDateForFirstPayment.setDate(initialEarliestDateForFirstPayment.getDate())
    return initialEarliestDateForFirstPayment
  }, [])

  return getProductsEarliestPaymentDateGivenActivationDate
}

export const useProductsFirstPaymentDateOptions = () => {
  const getProductsEarliestPaymentDateGivenActivationDate = useProductsEarliestPaymentDate()

  const computeProductsFirstPaymentDateOptions = useCallback(
    (activationDate: string, frequency: EPaymentFrequency) => {
      const activationDateAsDate = parse(activationDate, 'yyyy-MM-dd', new Date())
      const earliestPaymentDate = getProductsEarliestPaymentDateGivenActivationDate(activationDateAsDate)

      const firstPaymentDateOptionsList: Date[] = []
      const currentDate = new Date(earliestPaymentDate)
      const oneMonthLaterOrTwoWeeksLater =
        frequency === EPaymentFrequency.Monthly ? addMonths(earliestPaymentDate, 1) : addWeeks(earliestPaymentDate, 2)

      while (currentDate <= oneMonthLaterOrTwoWeeksLater) {
        if (isValid(currentDate)) {
          firstPaymentDateOptionsList.push(new Date(currentDate))
        }
        currentDate.setDate(currentDate.getDate() + 1)
      }

      return firstPaymentDateOptionsList
    },
    [getProductsEarliestPaymentDateGivenActivationDate],
  )

  return computeProductsFirstPaymentDateOptions
}

export const useTermOptionList = (termList: ETerms[]) => {
  const { t } = useTranslation()
  return useMemo(
    () =>
      termList.map((term) => (
        <MenuItem key={term} value={term}>
          {term} {t('common.months')}
        </MenuItem>
      )),
    [termList, t],
  )
}
